/**
 * Attendance colour-coding, per spec:
 *   0%        -> Grey  (No Data)
 *   1% - 69%  -> Red   (Poor)
 *   70% - 89% -> Yellow(Moderate)
 *   90% - 100%-> Green (Good)
 *
 * NOTE: 0% is treated as "No Data" per the written criteria, distinct from a
 * missing/undefined value (venue not yet reported). Both render grey, but
 * we keep the label different in case you want to distinguish them later.
 */

export type AttendanceStatusLabel = "Good" | "Moderate" | "Poor" | "No Data";

export interface IAttendanceStatus {
  label: AttendanceStatusLabel;
  color: string; // hex, used for map fill + badge dot
  bgClass: string; // tailwind bg for stat cards / badges
  textClass: string; // tailwind text colour
}

const STATUS_GOOD: IAttendanceStatus = {
  label: "Good",
  color: "#22C55E",
  bgClass: "bg-green-50",
  textClass: "text-green-600",
};

const STATUS_MODERATE: IAttendanceStatus = {
  label: "Moderate",
  color: "#F5A524",
  bgClass: "bg-amber-50",
  textClass: "text-amber-600",
};

const STATUS_POOR: IAttendanceStatus = {
  label: "Poor",
  color: "#EF4444",
  bgClass: "bg-red-50",
  textClass: "text-red-600",
};

const STATUS_NO_DATA: IAttendanceStatus = {
  label: "No Data",
  color: "#9CA3AF",
  bgClass: "bg-gray-100",
  textClass: "text-gray-500",
};

/**
 * Returns the status bucket for a given attendance percentage.
 * `undefined`/`null` (venue hasn't reported at all) is also treated as No Data.
 */
export const getAttendanceStatus = (
  percentage: number | undefined | null,
): IAttendanceStatus => {
  if (percentage === undefined || percentage === null) return STATUS_NO_DATA;
  if (percentage === 0) return STATUS_NO_DATA;
  if (percentage < 70) return STATUS_POOR;
  if (percentage < 90) return STATUS_MODERATE;
  return STATUS_GOOD;
};

/** Convenience: just the hex colour, for the choropleth map fill. */
export const getAttendanceColor = (percentage: number | undefined | null): string =>
  getAttendanceStatus(percentage).color;
